import { Card, Table, Button, Spinner } from "react-bootstrap";
import { FileEarmark } from "react-bootstrap-icons";
import { formatCurrency } from "../../../store/config";
import StatusBadge from "./StatusBadge";
import PaginationComponent from "./PaginationComponent";
import WithdrawalFilters from "./WithdrawalFilter";
import BeneficiaryRequestDetailsModal from "./BeneficiaryRequestDetailsModal";
import CommentsModal from "./CommentModal";
import ContentWrapper from "../../utilities/ContentWrapper";
import { useBeneficiaryWithdrawalRequests } from "../../hooks/useBeneficiaryWithdrawalRequest";
import { useBeneficiaryWithdrawalActions } from "../../hooks/useBeneficiaryWithdrawalAction";

function BeneficiaryWithdrawalManagement() {
    const {
        withdrawalRequests,
        isLoading,
        pagination,
        filterStatus,
        searchQuery,
        currentPage,
        setSearchQuery,
        handleFilterChange,
        handleSearch,
        handlePageChange,
        resetFilters,
    } = useBeneficiaryWithdrawalRequests();

    const {
        selectedRequest,
        showDetailsModal,
        showCommentsModal,
        processingAction,
        comments,
        setComments,
        setShowCommentsModal,
        handleViewDetails,
        handleUpdateStatus,
        closeModals,
    } = useBeneficiaryWithdrawalActions();

    const statusBadgeElement = (status) => <StatusBadge status={status} />;

    const updateRequestStatus = (status) => {
        handleUpdateStatus(status);
    };

    return (
        <ContentWrapper>
            {/* Filters */}
            <Card className="mb-4 shadow-sm">
                <Card.Body>
                    <WithdrawalFilters
                        filterStatus={filterStatus}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        handleFilterChange={handleFilterChange}
                        handleSearch={handleSearch}
                        resetFilters={resetFilters}
                    />
                </Card.Body>
            </Card>

            {/* Beneficiary withdrawal requests table */}
            <Card className="shadow-sm">
                <Card.Body className="p-0">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading beneficiary withdrawal requests...</p>
                        </div>
                    ) : withdrawalRequests?.length === 0 ? (
                        <div className="text-center py-5">
                            <FileEarmark size={48} className="text-muted mb-3" />
                            <h5>No beneficiary withdrawal requests found</h5>
                            <p className="text-muted">No requests match your current filters</p>
                        </div>
                    ) : (
                        <>
                            <Table responsive hover className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Date</th>
                                        <th>Deceased Member ID</th>
                                        <th>Beneficiary Name</th>
                                        <th>Date of Death</th>
                                        <th className="text-right">Amount Due</th>
                                        <th className="text-center">Status</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {withdrawalRequests.map((request) => (
                                        <tr key={request._id}>
                                            <td>{new Date(request.createdAt).toLocaleDateString()}</td>
                                            <td>{request.deceasedMemberId}</td>
                                            <td>{request.beneficiary?.name || "N/A"}</td>
                                            <td>
                                                {request.dateOfDeath ?
                                                    new Date(request.dateOfDeath).toLocaleDateString() :
                                                    "N/A"}
                                            </td>
                                            <td className="text-right">
                                                {formatCurrency(request.calculatedAmounts?.amountDue || request.calculatedAmounts?.netAmount || 0)}
                                            </td>
                                            <td className="text-center">
                                                {statusBadgeElement(request.status)}
                                            </td>
                                            <td className="text-center">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => handleViewDetails(request)}
                                                >
                                                    View Details
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <PaginationComponent
                                pagination={pagination}
                                currentPage={currentPage}
                                handlePageChange={handlePageChange}
                            />
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Modals */}
            {selectedRequest && (
                <>
                    <BeneficiaryRequestDetailsModal
                        show={showDetailsModal}
                        onHide={closeModals}
                        request={selectedRequest}
                        processingAction={processingAction}
                        handleUpdateStatus={handleUpdateStatus}
                        StatusBadge={StatusBadge}
                    />

                    <CommentsModal
                        show={showCommentsModal}
                        onHide={() => setShowCommentsModal(false)}
                        comments={comments}
                        setComments={setComments}
                        processingAction={processingAction}
                        onConfirm={() => updateRequestStatus("rejected")}
                    />
                </>
            )}
        </ContentWrapper>
    );
}

export default BeneficiaryWithdrawalManagement;